import type { KeyboardEvent, ReactNode } from 'react';
import type { Experiment } from 'types';
import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { buildContext } from './report-context';
import { useExperimentReport } from '@/state/experiments/useExperimentReport';
import { useReportRead } from '@/state/experiments/useReportRead';
import { REPORT_STEPS, applyRowVerdicts, type RowVerdict } from '@/state/experiments/report-questions';
import { ReadStep, checkableRows, type RowVerdicts } from './ReadStep';
import { ReportForm } from './ReportForm';
import { ResultStep } from './ResultStep';

const SECONDARY_BUTTON_CLASS =
  'border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta';
const PRIMARY_BUTTON_CLASS = 'bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white';

type ReportWizardProps = {
  experiment: Experiment;
  /** Close the wizard. Called after finishing, and once the user has confirmed they want to skip. */
  onClose: () => void;
  onViewReport: () => void;
};

type FooterProps = {
  index: number;
  isSaving: boolean;
  onBack: () => void;
  onSkip: () => void;
  onNext: () => void;
};

/**
 * Three zones: the way out on the left, progress in the middle, and the pair that moves
 * through the screens on the right. Skip used to sit between Back and Next, which read as a
 * third step of the same kind; apart from them it reads as leaving.
 */
const Footer = ({ index, isSaving, onBack, onSkip, onNext }: FooterProps) => {
  const isLast = index === REPORT_STEPS.length - 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pt-1">
      <Button variant="ghost" onClick={onSkip} className="text-dim hover:text-foreground -ml-2 h-8 px-2 text-meta">
        Skip for now
      </Button>

      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {REPORT_STEPS.map((step, position) => (
            <span
              key={step}
              className={cn('rounded-pill h-1 w-[18px]', position <= index ? 'bg-accent' : 'bg-track')}
            />
          ))}
        </div>
        <span className="text-dim font-mono text-[10px]">
          {index + 1} of {REPORT_STEPS.length}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {index > 0 && (
          <Button variant="outline" onClick={onBack} disabled={isSaving} className={SECONDARY_BUTTON_CLASS}>
            <ChevronLeft className="size-3.5" strokeWidth={1.5} />
            Back
          </Button>
        )}
        <Button onClick={onNext} disabled={isSaving} className={PRIMARY_BUTTON_CLASS}>
          {isSaving ? 'Saving…' : isLast ? 'Save debrief' : 'Next'}
          {!isSaving && !isLast && <ChevronRight className="size-3.5" strokeWidth={1.5} />}
        </Button>
      </div>
    </div>
  );
};

/** The verdicts that differ from the ones already applied, so a screen is never re-applied over later edits. */
const changedVerdicts = (verdicts: RowVerdicts, applied: RowVerdicts): RowVerdicts =>
  Object.fromEntries(
    Object.entries(verdicts).filter(([key, verdict]) => verdict !== applied[key as keyof RowVerdicts]),
  );

type PaddedProps = { children: ReactNode };
const Padded = ({ children }: PaddedProps) => <div className="flex flex-col gap-4 p-[22px]">{children}</div>;

/**
 * The optional prompt after someone concludes an experiment by hand, by ending it or by
 * deploying a winner, in three screens: the result and whether it held, Pertento's drafted
 * read of the run with what comes next, and a review of everything on one page before saving.
 *
 * Saved as the user moves on, so abandoning on screen 2 keeps screen 1's answer, and the
 * review screen saves whatever is left. Skipping, or closing any other way, saves nothing
 * further and never touches the experiment, which has already concluded by the time this opens.
 * Mounted only while open, so the report and its read are not fetched on every page view.
 */
export const ReportWizard = ({ experiment, onClose, onViewReport }: ReportWizardProps) => {
  const manager = useExperimentReport(experiment.id);
  const { read, isLoading: isReadLoading, isError: isReadError } = useReportRead({ experimentId: experiment.id });
  const [index, setIndex] = useState(0);
  const [verdicts, setVerdicts] = useState<RowVerdicts>({});
  const [appliedVerdicts, setAppliedVerdicts] = useState<RowVerdicts>({});
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);

  const step = REPORT_STEPS[index] ?? 'review';
  const context = buildContext(experiment);

  const setVerdict = (key: keyof RowVerdicts, verdict: RowVerdict) =>
    setVerdicts((current) => ({ ...current, [key]: verdict }));

  const next = async () => {
    if (step === 'result') {
      // Stay on the screen if the save failed: the toast says why, and the answer is still on screen.
      if (await manager.saveStep('result')) setIndex(1);
      return;
    }

    if (step === 'read') {
      // The confirmed and corrected lines decide the outside factors. Only the verdicts given or
      // changed since the last time are applied: going Back from the review screen and forward
      // again must not undo a chip ticked by hand there. Set on the draft as well as saved, so
      // the review screen shows them straight away.
      const outsideFactors = applyRowVerdicts({
        factors: manager.draft.outsideFactors,
        rows: checkableRows(read),
        verdicts: changedVerdicts(verdicts, appliedVerdicts),
      });
      manager.setAnswer('outsideFactors', outsideFactors);
      if (await manager.saveStep('read', { outsideFactors })) {
        setAppliedVerdicts(verdicts);
        setIndex(2);
      }
      return;
    }

    // Saving lands on the Debrief tab, where the saved report can be read and changed later.
    if (await manager.saveStep('review')) {
      onViewReport();
      onClose();
    }
  };

  const back = () => setIndex((current) => Math.max(current - 1, 0));

  // Enter continues on the first two screens, except where Enter means something else: a text
  // area takes a new line (Cmd or Ctrl plus Enter continues from there) and a button does what
  // it says. Never on the review screen, where it would save and close without a deliberate click.
  // Not while an input method is composing (Enter confirms the composition there) and not on key
  // repeat, which would carry a held key from one screen straight through the next.
  const onKeyDown = (ev: KeyboardEvent) => {
    if (ev.key !== 'Enter' || step === 'review' || manager.isSaving) return;
    if (ev.nativeEvent.isComposing || ev.repeat) return;
    const target = ev.target as HTMLElement;
    if (target.tagName === 'BUTTON') return;
    if (target.tagName === 'TEXTAREA' && !(ev.metaKey || ev.ctrlKey)) return;
    ev.preventDefault();
    next();
  };

  // Every way out (Skip for now, the X, Escape, a click outside) asks first, because leaving drops
  // whatever is on the current screen. Finishing, and viewing the debrief, close directly.
  const requestClose = () => setIsConfirmingClose(true);

  const footer = <Footer index={index} isSaving={manager.isSaving} onBack={back} onSkip={requestClose} onNext={next} />;

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && requestClose()}>
        <DialogContent
          onKeyDown={onKeyDown}
          className={cn(
            'border-line bg-panel sm:rounded-panel max-h-[90vh] gap-0 overflow-y-auto p-0',
            step === 'review' ? 'sm:max-w-[836px]' : 'sm:max-w-[560px]',
          )}
        >
          {step === 'result' && (
            <Padded>
              <ResultStep
                manager={manager}
                experimentName={experiment.name}
                read={read}
                isLoading={isReadLoading}
                isError={isReadError}
              />
              {footer}
            </Padded>
          )}

          {step === 'read' && (
            <Padded>
              <ReadStep manager={manager} read={read} verdicts={verdicts} onVerdict={setVerdict} />
              {footer}
            </Padded>
          )}

          {step === 'review' && (
            <ReportForm
              manager={manager}
              context={context}
              read={read}
              heading={
                <div className="flex flex-col gap-1">
                  <DialogTitle className="text-foreground text-title font-medium">
                    What happened with this test?
                  </DialogTitle>
                  <DialogDescription className="supporting">
                    Every answer is optional and lands in the card on the right.
                  </DialogDescription>
                </div>
              }
              footer={footer}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        title="Skip the debrief for now?"
        open={isConfirmingClose}
        onConfirm={onClose}
        onCancel={() => setIsConfirmingClose(false)}
        confirmLabel="Skip for now"
      >
        <p className="supporting">
          You can fill in the debrief any time from the Debrief tab on this experiment. Answers from the screens you
          have already moved past are saved; what is on this screen is not.
        </p>
      </ConfirmDialog>
    </>
  );
};
